import { Avatar, Chip, Snackbar, Alert } from '@mui/material';
import axios from 'axios';
import {
  MemreButton,
  MemreCollapseText,
  MemreFlexBox,
  MemreIcon,
  MemreProgressBar,
  MemreSection,
  MemreSectionHeading,
  MemreText,
  IconName,
} from 'components/core';
import UnenrollConfirmationDialog from 'components/modals/UnenrollConfirmationDialog';
import * as Color from 'core/colors';
import { useUnenroll } from 'hooks/useUnenroll';
import { useState } from 'react';
import { pluralize } from 'utils/stringUtils';

export default function CourseDetails({ course, user }) {
  // State for enrollment operations
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('info');

  // Course metadata
  const isEnrolled = course?.meta?.isEnrolled;
  const isPaid = course?.meta?.paymentRequired;
  const canUnenroll = course?.meta?.canUnenroll ?? false;
  const coursePercentage = Math.round((course?.meta?.progress || 0) * 100);

  // Initialize the unenroll hook
  const {
    isLoading: isUnenrolling,
    error: unenrollError,
    errorMessage,
    isConfirmationOpen,
    openConfirmation,
    closeConfirmation,
    executeUnenroll,
  } = useUnenroll({
    // After successful unenrollment
    onSuccess: () => {
      setSnackbarMessage('Successfully unenrolled from course');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
      // Navigate to dashboard after a short delay
      setTimeout(() => {
        window.location.href = '/app/nav/v4/dashboard';
      }, 1500);
    },
    // If unenrollment fails
    onError: () => {
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  });

  // Handle enrollment
  const enroll = async () => {
    if (!user) return;

    setIsEnrolling(true);
    try {
      await axios.post(`/api/v3/courses/${course.id}/users`, {
        course_id: Number(course.id),
        user_id: Number(user.id),
      });

      // Show success message
      setSnackbarMessage('Successfully enrolled in course');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);

      // Reload the page to show enrolled state
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      console.error('Error enrolling in course:', error);
      
      // Show error message
      setSnackbarMessage('Failed to enroll in course. Please try again.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsEnrolling(false);
    }
  };

  // Handle unenroll button click
  const handleUnenrollClick = () => {
    if (course && user) {
      openConfirmation(course.id, user.id);
    }
  };

  // Handle snackbar close
  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  return (
    <MemreSection>
      <MemreSectionHeading text="Course Details" />

      {/* Unenroll confirmation dialog */}
      <UnenrollConfirmationDialog
        open={isConfirmationOpen}
        isLoading={isUnenrolling}
        courseName={course?.name || 'this course'}
        onConfirm={executeUnenroll}
        onCancel={closeConfirmation}
      />

      {/* Feedback snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbarSeverity} 
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <MemreFlexBox direction="column" sx={{ p: 3 }}>
        <MemreFlexBox gap={6} direction={{ xs: 'column', md: 'row' }}>
          <MemreFlexBox direction="column" gap={3} sx={{ flex: 1 }}>
            {/* Course description */}
            <MemreCollapseText text={course?.description || 'No description available.'} />

            {/* Course metadata */}
            <MemreFlexBox gap={2} sx={{ '& > *': { mr: 1 } }}>
              {isPaid && <Chip label="Paid Course" color="secondary" variant="outlined" size="small" />}
              {isEnrolled && <Chip label="Enrolled" color="success" variant="outlined" size="small" />}
              {course?.assignmentsCount > 0 && (
                <Chip
                  label={pluralize(course.assignmentsCount, 'Assignment')}
                  color="primary"
                  variant="outlined"
                  size="small"
                />
              )}
            </MemreFlexBox>
          </MemreFlexBox>

          <MemreFlexBox gap={3} direction="column" sx={{ minWidth: { xs: '100%', md: '340px' } }}>
            {/* Organization info */}
            <MemreFlexBox direction="row" gap={2} align="center">
              {course?.partner?.image?.smallUrl ? (
                <Avatar src={course?.partner?.image?.smallUrl} />
              ) : (
                <MemreIcon iconName={IconName.building} color={Color.veryLightGray} fontSize="1.9em" />
              )}
              <MemreFlexBox direction="column">
                <MemreText variant="overline">Organization</MemreText>
                <MemreText>{course?.partner?.name}</MemreText>
              </MemreFlexBox>
            </MemreFlexBox>

            {/* Instructor info */}
            <MemreFlexBox direction="row" gap={2} align="center">
              <MemreIcon iconName={IconName.user} color={Color.veryLightGray} fontSize="1.9em" />
              <MemreFlexBox direction="column">
                <MemreText variant="overline">Instructors</MemreText>
                <MemreText>
                  {!course?.instructorNames || course?.instructorNames?.length === 0
                    ? 'This course has no instructors'
                    : course?.instructorNames.filter(Boolean).join(', ')}
                </MemreText>
              </MemreFlexBox>
            </MemreFlexBox>

            {/* Enrollment actions */}
            <MemreFlexBox direction="row" justify="flex-end" gap={2} sx={{ mt: 2 }}>
              {isEnrolled ? (
                canUnenroll && (
                  <MemreButton
                    text="Unenroll"
                    size="medium"
                    color="warning"
                    variant="outlined"
                    iconName="trash"
                    onClick={handleUnenrollClick}
                    disabled={isUnenrolling}
                  />
                )
              ) : (
                <MemreButton
                  text={isPaid ? 'Purchase & Enroll' : 'Enroll Now'}
                  size="medium"
                  color="primary"
                  variant="contained"
                  iconName="bookmark"
                  onClick={enroll}
                  disabled={isEnrolling}
                  sx={{ minWidth: '150px' }}
                />
              )}
            </MemreFlexBox>
          </MemreFlexBox>
        </MemreFlexBox>

        {/* Progress section - only show for enrolled users */}
        {isEnrolled && (
          <MemreFlexBox gap={1} direction="column" sx={{ mt: 4 }}>
            <MemreText>
              Progress: <b>{coursePercentage}%</b>
            </MemreText>
            <MemreProgressBar progress={coursePercentage} hideNumber={true} />
          </MemreFlexBox>
        )}
      </MemreFlexBox>
    </MemreSection>
  );
}
